import { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../../constants";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  FormLayout,
  Banner,
  Form,
  Button,
  TextField,
  Select,
  Checkbox,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { CustomField } from "types/CustomField";
import DatePickerx from "features/datepicker/date-picker";
import { Product } from "types/Product";
import { useAppDispatch } from "hooks";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import ProductsSelect from "components/products-select/ProductsSelect";
import ProductGroupSelect from "components/products-select/ProductGroupSelect";
import { fetchFileDetails } from "containers/file/file.Slice";
import AddressSelectMultiple from "features/customer-address-select-multiple/customer-address-select-multiple";
import UserSelect from "features/user-select/user-select";
import CustomFieldFileUpload from "components/custom-field-file-upload/custom-field-file-upload";
import { getAllCountryOptions } from "constants/countries";
import {
  parseNumericFieldValue,
  isNumberOutsideFieldRange,
} from "./build-pdf-utils";

interface onSubmitData {
  templateId: string;
  date: string;
  customFields: { [key: string]: string | string[] };
  products: Product[];
  productGroups?: any[];
  fileId?: string;
  signersInfo?: {};
  address?: String[];
}

interface PostData extends onSubmitData {
  customerId: string;
}

function BuildPdfForm({
  customerId,
  isCustomerApproved = false,
  template = "",
  fileId = "",
  signersInfo,
  restrictions,
}: {
  customerId: string;
  isCustomerApproved: boolean;
  template: string;
  fileId: string;
  signersInfo: any;
  restrictions: string[];
}) {
  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const { companyId } = useParams();
  const dispatch = useAppDispatch();
  const normalisedSignersInfo = (() => {
    if (!signersInfo) {
      return undefined;
    }
    const { contactSigner, contactSigners, ...rest } = signersInfo || {};
    const resolvedContactSigners = contactSigners
      ? contactSigners
      : contactSigner
      ? [contactSigner]
      : [];
    return {
      ...rest,
      contactSigners: resolvedContactSigners,
    };
  })();

  const { t } = useTranslation();
  const isFormValid = () => {
    // Check if templateId is missing
    if (!templateId) {
      console.log("Form is invalid: Missing templateId");
      return false;
    }

    if (!date) {
      console.log("Form is invalid: Missing date");
      return false;
    }

    // Check custom fields
    const invalidFields = customFields.filter((field) => {
      // Check if required field is empty
      if (field.required) {
        if (field.type === "file") {
          const fileIds = Array.isArray(field.value)
            ? field.value
            : field.value
            ? [field.value]
            : [];
          if (fileIds.length === 0) {
            return true;
          }
        } else if (!field.value || field.value.length === 0) {
          // Catches empty strings "" and empty arrays [] for multi-select checkboxes
          return true;
        }
      }

      // Check minimum length for string and textarea fields
      if (
        field.minLength &&
        field.minLength > 0 &&
        field.value &&
        (field.type === "string" || field.type === "textarea")
      ) {
        if (
          typeof field.value === "string" &&
          field.value.length < field.minLength
        ) {
          return true;
        }
      }

      // Check number range when min/max set
      if (field.type === "number" && (field.min != null || field.max != null)) {
        const num = parseNumericFieldValue(field);
        if (!Number.isNaN(num) && isNumberOutsideFieldRange(field, num)) {
          return true;
        }
      }

      return false;
    });

    if (invalidFields.length > 0) {
      console.log(
        "Form is invalid: The following fields are missing or incomplete:"
      );
      invalidFields.forEach((field) => {
        console.log(
          `- Field name: ${field.name || "Unnamed Field"}, Type: ${field.type}`
        );
      });
      return false;
    }

    // If everything is valid
    return true;
  };

  const fetchCustomFields = useCallback(
    async (templateId: string) => {
      try {
        const fileTemplateEntity = await fetchFileTemplateById(
          companyId,
          templateId
        );
        setCustomFields(
          fileTemplateEntity.customFields.map((field: CustomField) => ({
            ...field,
            value:
              field.type === "file" ? field.value || [] : field.value || "",
          }))
        );
      } catch (e) {
        console.error(e);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (templateId) {
      fetchCustomFields(templateId);
    }
  }, [templateId, fetchCustomFields]);

  // Helper to get string value for non-file fields
  const getStringValue = (field: CustomField): string => {
    if (field.type === "file") {
      return "";
    }
    return Array.isArray(field.value) ? "" : field.value || "";
  };

  const handleCustomFieldChange = (index: number, value: string | string[]) => {
    const updatedFields = customFields.map((field, i) =>
      i === index ? { ...field, value } : field
    );
    setCustomFields(updatedFields);
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) {
      return;
    }
    const formData: PostData = {
      date,
      templateId,
      customerId,
      customFields: {},
      products,
      productGroups,
      fileId,
      signersInfo: normalisedSignersInfo,
    };
    customFields.forEach((field) => {
      if (field.type === "file") {
        // For file fields, ensure we send an array of file IDs
        const fileIds = Array.isArray(field.value)
          ? field.value
          : field.value
          ? [field.value]
          : [];
        formData.customFields[field.label] = fileIds;
      } else {
        formData.customFields[field.label] = field.value;
      }
    });

    if (companyId) {
      try {
        setIsLoading(true);
        const fileEntity = await submit(companyId, formData);
        if (fileId) dispatch(fetchFileDetails({ fileId, companyId }));
        if (fileEntity.id) {
          if (customerId) dispatch(fetchCustomer({ companyId, customerId }));
          setIsSuccessful(true);
          setTimeout(() => {
            dispatch(setIsModalxOpen(false));
          }, 1500);
        } else {
          setIsFailed(true);
          setIsLoading(false);
        }
      } catch (e) {
        setIsFailed(true);
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="user-form">
      <Form onSubmit={handleFormSubmit}>
        <div className="form-warnings">
          {isFailed && (
            <div className="">
              <Banner tone="critical">{t("error")}</Banner>
            </div>
          )}
          {isSuccessful && (
            <div className="">
              <Banner tone="info">{t("ok")}</Banner>
            </div>
          )}
        </div>
        <FormLayout>
          <FileTemplateSelect
            onChange={setTemplateId}
            value={templateId}
            isRequired={true}
            isCustomerApproved={isCustomerApproved}
            templateId={template || ""}
            restrictions={restrictions}
          />
          {templateId && (
            <DatePickerx
              disableDatesAfter={null}
              title={t("document_date")}
              isRequired={true}
              onChange={(value: any) =>
                setDate(value.toISOString().split("T")[0])
              }
              selected={date}
            />
          )}
          {customFields.map((field, index) => (
            <div key={field.id}>
              {field.type === "string" && (
                <TextField
                  label={field.name}
                  value={getStringValue(field)}
                  autoComplete="off"
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  requiredIndicator={field.required}
                  helpText={field.helperText}
                />
              )}
              {field.type === "number" && (
                <TextField
                  label={field.name}
                  value={getStringValue(field)}
                  autoComplete="off"
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value.replace(/[^0-9]/g, ""))
                  }
                  requiredIndicator={field.required}
                />
              )}
              {field.type === "textarea" && (
                <TextField
                  label={field.name}
                  value={getStringValue(field)}
                  multiline={4}
                  autoComplete="off"
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  requiredIndicator={field.required}
                  helpText={field.helperText}
                />
              )}
              {field.type === "date" && (
                <DatePickerx
                  disableDatesAfter={null}
                  title={field.name}
                  isRequired={field.required}
                  onChange={(value: any) =>
                    handleCustomFieldChange(
                      index,
                      value.toISOString().split("T")[0]
                    )
                  }
                  selected={getStringValue(field)}
                />
              )}
              {field.type === "select" && (
                <Select
                  label={field.name}
                  options={[
                    {
                      label: t(
                        "files_template_custom_type_select_select_an_option"
                      ),
                      value: "",
                    },
                    ...field.options.map((option) => ({
                      label: option,
                      value: option,
                    })),
                  ]}
                  value={getStringValue(field)}
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  requiredIndicator={field.required}
                />
              )}
              {field.type === "products" && (
                <ProductsSelect
                  onChange={(value: any) => {
                    if (JSON.stringify(products) !== JSON.stringify(value)) {
                      handleCustomFieldChange(index, value);
                      setProducts(value);
                    }
                  }}
                />
              )}
              {field.type === "product_group" && (
                <ProductGroupSelect
                  onProductGroupsChange={(groups: any[]) => {
                    if (
                      JSON.stringify(productGroups) !== JSON.stringify(groups)
                    ) {
                      handleCustomFieldChange(index, groups as any);
                      setProductGroups(groups);
                    }
                  }}
                  initialProductGroups={productGroups}
                  isRequired={field.required}
                />
              )}
              {field.type === "address" && (
                <AddressSelectMultiple
                  value={
                    Array.isArray(field.value) ? field.value : [field.value]
                  }
                  onChange={(value: any) => {
                    handleCustomFieldChange(index, value);
                  }}
                  customerId={customerId}
                />
              )}
              {field.type === "user" && (
                <UserSelect
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  value={getStringValue(field)}
                  label={field.name}
                />
              )}
              {field.type === "signature" && (
                <UserSelect
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  value={getStringValue(field)}
                  label={field.name}
                />
              )}
              {field.type === "country" && (
                <Select
                  label={field.name}
                  options={[
                    {
                      label: t(
                        "files_template_custom_type_select_select_an_option"
                      ),
                      value: "",
                    },
                    ...getAllCountryOptions(t),
                  ]}
                  value={getStringValue(field)}
                  onChange={(value: string) =>
                    handleCustomFieldChange(index, value)
                  }
                  requiredIndicator={field.required}
                />
              )}
              {field.type === "checkbox" && (
                <div>
                  {field.options && field.options.length > 0 ? (
                    <>
                      {field.name && (
                        <Text as="p" variant="bodyMd">
                          {field.name}
                        </Text>
                      )}
                      {field.options.map((option) => {
                        const currentValue = Array.isArray(field.value)
                          ? field.value
                          : field.value
                          ? [field.value]
                          : [];
                        const checked = currentValue.includes(option);
                        return (
                          <Checkbox
                            key={option}
                            label={option}
                            checked={checked}
                            onChange={(isChecked: boolean) => {
                              let newValue: string | string[];
                              if (field.isMulti) {
                                const base = currentValue;
                                if (isChecked) {
                                  newValue = Array.from(
                                    new Set([...base, option])
                                  );
                                } else {
                                  newValue = base.filter((v) => v !== option);
                                }
                              } else {
                                newValue = isChecked ? option : "";
                              }
                              handleCustomFieldChange(index, newValue);
                            }}
                          />
                        );
                      })}
                    </>
                  ) : (
                    <Checkbox
                      label={field.name}
                      checked={!!field.value}
                      onChange={(isChecked: boolean) =>
                        handleCustomFieldChange(index, isChecked ? "1" : "")
                      }
                    />
                  )}
                </div>
              )}
              {field.type === "file" && (
                <CustomFieldFileUpload
                  label={field.name}
                  value={field.value}
                  onChange={(fileIds: string[]) =>
                    handleCustomFieldChange(index, fileIds)
                  }
                  isRequired={field.required}
                />
              )}
            </div>
          ))}
          <Button loading={isLoading} disabled={!isFormValid()} submit>
            {t("save")}
          </Button>
        </FormLayout>
      </Form>
    </div>
  );
}

export default BuildPdfForm;

function submit(companyId: string, data: PostData) {
  const url = `${API_URL}/${companyId}/files/build-pdf`;
  return axios.post(url, data).then((response) => response.data);
}
